import React, { useState } from "react";
import { ENV_VERIFIED_TRAINER, isBrandActive } from "src/config/environment.config";
import { normalizeSessionDescriptionOverride, sessionDescriptionOverrideError } from "../../../Utils/sessionDescriptionOverride";
import { useTravelScenario } from "../../DeclarationCourse/Travel/TravelModeSelector";
import { useSessionDescriptionCheck } from "../../DeclarationCourse/useSessionDescriptionCheck";
import { DV2 } from "../theme";
import { studentFirstName } from "../format";
import { vocab } from "../vocabulary";
import { useDeclarationV2 } from "../DeclarationV2Context";
import WizardShell from "../layout/WizardShell";
import StepsRail from "../layout/StepsRail";
import StudentMonthPanel from "../layout/StudentMonthPanel";
import { Button, Badge } from "../layout/Ui";
import StudentTable from "../components/StudentTable";
import SessionDescriptionPanel from "../components/SessionDescriptionPanel";
import useStudentSelection from "../components/useStudentSelection";

/**
 * Étape 1 (maquettes 1a et 2a) : choix de l'élève dans un tableau, encart du
 * mois, déroulement du cours, puis « Continuer avec Prénom ». Avant de passer
 * au planning : description habituelle obligatoire (popup vers Paramètres) et
 * scénario de déplacement (mode, attestation, preuve), comme dans l'ancien
 * parcours.
 */
const StudentStep = () => {
    const { teachr, state, update, goTo, clients, totalClients, currentPage, pageSize, search, setSearch, setPage, clientsLoading } = useDeclarationV2();
    const { select, checkingId } = useStudentSelection(teachr);
    const { checkTravelScenario, travelModals, travelLoading } = useTravelScenario(teachr);
    const { checkSessionDescription, descriptionModal, descriptionLoading } = useSessionDescriptionCheck(teachr);
    const [generic, setGeneric] = useState<string | null>(null);
    const [error, setError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");

    const student = state.student;
    const override = state.sessionDescriptionOverride;

    const handleSelect = async (child: any) => {
        setError("");
        const selected = await select(child);
        if (selected) {
            update({ student: selected });
        }
    };

    const proceed = () => {
        if (!student) {
            setError(`Choisissez ${vocab.theClient()} concerné avant de continuer.`);
            return;
        }
        const overrideError = sessionDescriptionOverrideError(override);
        setDescriptionError(overrideError);
        if (overrideError) return;
        const normalized = normalizeSessionDescriptionOverride(override, generic);
        checkSessionDescription(() =>
            checkTravelScenario(student.child, () => {
                update({ sessionDescriptionOverride: normalized, step: "planning" });
                goTo("planning");
            })
        );
    };

    const courseTypeLabel = state.courseType === "multiple" ? vocab.multipleSessionsTitle() : vocab.singleCourseTitle();

    return (
        <>
            {travelModals}
            {descriptionModal}
            <WizardShell
                title={`Pour quel ${vocab.client()} ?`}
                subtitle="Étape 1 sur 4"
                onBack={() => goTo("home")}
                aside={
                    <>
                        <StepsRail
                            current={1}
                            steps={[
                                { number: 1, label: vocab.client().charAt(0).toUpperCase() + vocab.client().slice(1) },
                                { number: 2, label: "Planning" },
                                { number: 3, label: `Tarif ${!isBrandActive(ENV_VERIFIED_TRAINER) && `& ${vocab.subjectLabel()}`}`, },
                                { number: 4, label: "Récapitulatif" },
                            ]}
                        />
                        {student ? (
                            <StudentMonthPanel child={student.child} teachr={teachr} />
                        ) : (
                            <div style={{ fontSize: 12, color: DV2.placeholder, lineHeight: 1.45, padding: "0 4px" }}>
                                L'encart du mois apparaît dès qu'un {vocab.client()} est coché dans le tableau.
                            </div>
                        )}
                    </>
                }
            >
                <div className="flex items-center gap-2 flex-wrap" style={{ fontSize: 14 }}>
                    <Badge>{courseTypeLabel}</Badge>
                    <button type="button" onClick={() => goTo("home")} style={{ background: "none", border: "none", padding: 0, color: DV2.muted, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Changer le type de {vocab.course()}</button>
                </div>

                <StudentTable
                    teachr={teachr}
                    clients={clients}
                    total={totalClients}
                    page={currentPage}
                    pageSize={pageSize}
                    search={search}
                    loading={clientsLoading}
                    checkingId={checkingId}
                    selectedChildId={student?.childId ?? null}
                    onSearch={setSearch}
                    onPage={setPage}
                    onSelect={handleSelect}
                    renderSelectedExtra={(child) => (
                        <div className="lg:hidden">
                            <StudentMonthPanel child={child} teachr={teachr} compact />
                        </div>
                    )}
                />
                {error && <div role="alert" style={{ color: DV2.danger, fontSize: 13, fontWeight: 700 }}>{error}</div>}

                <SessionDescriptionPanel
                    teachr={teachr}
                    value={override}
                    onChange={(next) => { update({ sessionDescriptionOverride: next }); setDescriptionError(""); }}
                    onGenericLoaded={setGeneric}
                    error={descriptionError}
                />

                <div className="flex justify-end pt-1">
                    <Button
                        label={student ? `Continuer avec ${studentFirstName(student.child)}` : "Continuer"}
                        onClick={proceed}
                        disabled={!student || travelLoading || descriptionLoading}
                        loading={travelLoading || descriptionLoading}
                    />
                </div>
            </WizardShell>
        </>
    );
};

export default StudentStep;
